/**
 * Custom hook backing the Candid upload button.
 *
 * Filters auth-on-file J-codes down to those eligible for Candid upload,
 * derives the button label / status icon / disabled flag from the
 * `candid_rpa_status` on the order, and exposes a click handler that
 * opens the Auth Letter Upload modal seeded with grouped authorisation
 * data via Redux metaData.
 */
import { ReactNode, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { buildAuthLetterSuccessMetaData } from "../../../../../../components/modals/authLetterSuccessModal/utils/buildAuthLetterSuccessMetaData";
import { groupJCodesByAuthorization } from "../../../../../../components/modals/authLetterUploadModal/candidAuthorizationPayload";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../../data-model/medicalPaOrdersModel";
import { EVWriteBackStatus } from "../../../../../../enums/evBvWriteBackStatus";
import { MedicalPaOrdersAuthStatus } from "../../../../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../../../../enums/modalId";
import { setOpenedModalId } from "../../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../../redux/store/store";
import TaskMessageErrorIcon from "../../../../../../svg/taskMessageErrorIcon";
import TaskMessagePendingIcon from "../../../../../../svg/taskMessagePendingIcon";
import { getOrgIdForFetchExternalWorklist } from "../../../../../../utils/organizationHelper";
import { getAllJCodesFromAuthOnFile } from "../../../../utils/existingDocsToLs";

interface UseCandidUploadButtonProps {
  authOnFileDetails?: AuthOnFile;
  generalOrderDetails?: GeneralOrderDetails;
  primaryPayerName?: string;
}

/** Visual style of the upload button, mirroring the oasis-ui-kit Button. */
type CandidButtonType = "primary" | "secondary";

interface UseCandidUploadButtonReturn {
  buttonText: string;
  statusIcon: ReactNode;
  isDisabled: boolean;
  buttonType: CandidButtonType;
  onClick: () => void;
}

/** J-code auth statuses eligible to be uploaded to Candid. */
export const CANDID_ELIGIBLE_AUTH_STATUSES: MedicalPaOrdersAuthStatus[] = [
  MedicalPaOrdersAuthStatus.AuthOnFile,
  MedicalPaOrdersAuthStatus.AuthByRISA,
];

/**
 * RPA statuses where an upload has already been kicked off, so the
 * button should de-emphasise to a secondary style.
 */
const SECONDARY_BUTTON_STATUSES: (EVWriteBackStatus | undefined)[] = [
  EVWriteBackStatus.QUEUED,
  EVWriteBackStatus.IN_PROGRESS,
  EVWriteBackStatus.SUCCESS,
  EVWriteBackStatus.ERROR,
];

/**
 * Derives the Candid upload button's label, icon, disabled flag, and
 * click handler from order/auth-on-file data and the order's RPA status.
 *
 * @param props.authOnFileDetails - Auth-on-file payload for the order.
 * @param props.generalOrderDetails - General order details (provides RPA status and patient MRN).
 * @param props.primaryPayerName - Resolved primary payer name to seed the modal with.
 */
export const useCandidUploadButton = ({
  authOnFileDetails,
  generalOrderDetails,
  primaryPayerName,
}: UseCandidUploadButtonProps): UseCandidUploadButtonReturn => {
  const dispatch = useDispatch<AppDispatch>();

  const candidRpaStatus = generalOrderDetails?.status?.candid_rpa_status;

  /** J-codes whose auth status makes them eligible for Candid upload. */
  const eligibleJCodes = useMemo(
    () =>
      getAllJCodesFromAuthOnFile(authOnFileDetails)?.filter((jCode) =>
        CANDID_ELIGIBLE_AUTH_STATUSES.includes(
          jCode?.auth_status as MedicalPaOrdersAuthStatus,
        ),
      ),
    [authOnFileDetails],
  );

  const buttonText = useMemo(() => {
    if (candidRpaStatus === EVWriteBackStatus.IN_PROGRESS) {
      return "Uploading...";
    }
    if (candidRpaStatus === EVWriteBackStatus.SUCCESS) {
      return "View Candid Auth";
    }
    if (candidRpaStatus === EVWriteBackStatus.ERROR) {
      return "Retry";
    }
    return "Upload to Candid";
  }, [candidRpaStatus]);

  const statusIcon = useMemo<ReactNode>(() => {
    if (candidRpaStatus === EVWriteBackStatus.IN_PROGRESS) {
      return <TaskMessagePendingIcon />;
    }
    if (candidRpaStatus === EVWriteBackStatus.ERROR) {
      return <TaskMessageErrorIcon className="stroke-[1.5]" />;
    }
    return null;
  }, [candidRpaStatus]);

  const isDisabled = candidRpaStatus === EVWriteBackStatus.IN_PROGRESS;

  /**
   * Secondary once an upload has been queued/started/finished/failed;
   * primary when no Candid upload has been initiated yet.
   */
  const buttonType: CandidButtonType = SECONDARY_BUTTON_STATUSES.includes(
    candidRpaStatus as EVWriteBackStatus,
  )
    ? "secondary"
    : "primary";

  const onClick = useCallback(() => {
    if (candidRpaStatus === EVWriteBackStatus.SUCCESS) {
      const authLetterSuccessMetaData = buildAuthLetterSuccessMetaData(
        eligibleJCodes ?? [],
        primaryPayerName ?? "",
        generalOrderDetails?.demographics?.patient_id ?? "",
        generalOrderDetails?.status?.meta?.candid_rpa_success_timestamp ?? "",
      );
      dispatch(
        setOpenedModalId({
          id: ModalId.AUTH_LETTER_SUCCESS_MODAL,
          metaData: authLetterSuccessMetaData,
        }),
      );
      return;
    }

    const orgId =
      generalOrderDetails?.org_id ?? getOrgIdForFetchExternalWorklist() ?? "";
    const orderId = generalOrderDetails?.order_id ?? "";
    const payerName = primaryPayerName ?? "";

    dispatch(
      setOpenedModalId({
        id: ModalId.AUTH_LETTER_UPLOAD_MODAL,
        metaData: {
          authorizationData: groupJCodesByAuthorization(eligibleJCodes ?? []),
          orderId,
          orgId,
          mrn:
            generalOrderDetails?.demographics?.patient_id ??
            generalOrderDetails?.demographics?.patient_mrn ??
            "",
          payerName,
        },
      }),
    );
  }, [
    dispatch,
    candidRpaStatus,
    eligibleJCodes,
    primaryPayerName,
    generalOrderDetails?.demographics?.patient_id,
    generalOrderDetails?.demographics?.patient_mrn,
    generalOrderDetails?.org_id,
    generalOrderDetails?.order_id,
    generalOrderDetails?.status?.meta?.candid_rpa_success_timestamp,
  ]);

  return { buttonText, statusIcon, isDisabled, buttonType, onClick };
};
